import * as React from 'react';
import { translate } from 'react-i18next';
import Dropzone from 'react-dropzone';

import { ICropperOptions } from 'models/common';
import * as c from 'models/constants';
import { LoadingOverlayWrapper } from 'components';
require('./style.css');

import { CropTool } from 'components/Common/CropTool';
import { PortalFieldLabel } from '..';
import { formatInteger } from 'helpers/common';

interface IProps {
	existingImageId: string;
	existingImageUrl: string;
	meta: any;
	cropImage: boolean;
	cropOptions?: ICropperOptions;
	maxImageSize?: number;
	readOnly?: boolean;
	hideImageInput?: boolean;

	/**
	 * Do not remove dropzone, just hide it.
	 */
	forceKeepDropzone?: boolean;

	className?: string;

	input: any;
	name: string;
	label?: string;
	onClick?: (event: any) => void;
	/* pass additional data to image upload component (ex. property names of id, href properties on object) */
	additionalData: any;
	/* show only plain image input without loader and additional styles: */
	plainInput?: boolean;

	// pass ref to use in parent
	dropZoneRef?: any;

	imageDescription?: string;
	uploadDescription?: string;
	uploadImageIcon?: JSX.Element;
	customUploadAction?: () => void;

	/* functions props */
	imageUploadedCallback?: (id: string, href: string, additionalData?: any) => void;
	imageUploadFailed?: (error: any) => void;
	onDelete?: (event: any, index) => void;
	t?: (key: string, options?: any) => string;
	onFileDialogCancel?: () => void;
}

interface IState {
	currentImageId?: string;
	currentImageUrl?: string;
	loading?: boolean;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ImageUpload extends React.Component<IProps, IState> {
	private isCancelled: boolean = false;

	constructor(props) {
		super(props);

		this.handleDeleteImage = this.handleDeleteImage.bind(this);

		this.state = {
			loading: false,
			currentImageId: props.existingImageId ? props.existingImageId : null,
			currentImageUrl: props.existingImageUrl ? props.existingImageUrl : null,
		};
	}

	public componentDidUpdate() {
		if (this.props && this.state.currentImageUrl !== this.props.existingImageUrl) {
			this.setState({
				currentImageId: this.props.existingImageId,
				currentImageUrl: this.props.existingImageUrl,
			});
		}
	}

	public componentWillUnmount() {
		this.isCancelled = true;
	}

	private handleDeleteImage(event, index) {
		this.props.onDelete(event, index);
	}

	public uploadImage(data: any) {
		return (_, getState) => {
			const formData = new FormData();
			formData.append('file', data[0]);

			if (!this.isCancelled) {
				this.setState({ loading: true });
			}

			return fetch(getState().fetchDefault.baseAPI + 'api/imageupload/', {
				method: 'POST',
				body: formData,
				credentials: 'include',
				headers: {
					Authorization: getState().fetchDefault.requestInit.headers.Authorization,
				},
			})
				.then((res) => {
					if (res.ok) {
						return res.json().then((res: { id: string; href: string }) => {
							if (this.props.imageUploadedCallback) {
								this.props.imageUploadedCallback(res.id, res.href, this.props.additionalData);
							}
							if (!this.isCancelled) {
								this.setState({ loading: false });
							}
						});
					} else {
						if (!this.isCancelled) {
							this.setState({ loading: false });
						}
						const status: number = res.status;
						const statusText: string = res.statusText;
						return res
							.json()
							.then((res) => {
								res.status = status;
								res.statusText = statusText;
								if (this.props.imageUploadFailed) {
									this.props.imageUploadFailed(res);
								}
							})
							.catch(() => {
								const message: any = { status, statusText };
								if (!this.isCancelled) {
									this.setState({ loading: false });
								}
								if (this.props.imageUploadFailed) {
									this.props.imageUploadFailed(message);
								}
							});
					}
				})
				.catch((err) => {
					if (!this.isCancelled) {
						this.setState({ loading: false });
					}
					if (this.props.imageUploadFailed) {
						this.props.imageUploadFailed(err);
					}
				});
		};
	}

	public render() {
		const field = this.props;
		const {
			t,
			cropImage,
			onDelete,
			label,
			input,
			plainInput,
			cropOptions,
			maxImageSize,
			readOnly,
			imageDescription,
			uploadDescription,
			uploadImageIcon,
			className,
			customUploadAction,
		} = this.props;
		const { currentImageUrl } = this.state;

		// show loader
		const renderLoader = false;

		let pictureUrl = '';
		if (currentImageUrl) {
			pictureUrl = currentImageUrl;
		}

		// show crop image control if enabled
		let pictureOrCrop = null;
		if (pictureUrl.length > 0 && cropImage && cropOptions) {
			pictureOrCrop = <CropTool cropOptions={cropOptions} imageUrl={pictureUrl} />;
		} else if (pictureUrl.length > 0) {
			pictureOrCrop = <div className="Image" style={{ backgroundImage: `url(${pictureUrl})` }} />;
		}

		// if delete function exists and picture is uploaded, show delete image button, otherwise show image upload control
		let imageInput = null;
		if (!readOnly) {
			if (onDelete && pictureUrl.length > 0) {
				imageInput = (
					<div className="buttonHolder">
						<button
							title="delete image button"
							type="button"
							onClick={(event) => this.handleDeleteImage(event, this.props.additionalData)}
						>
							<img
								alt="delete image icon"
								src={require('containers/Html/icons/magazineAdmin/articleEditor/delete_image.svg')}
								className="icon"
							/>
						</button>
					</div>
				);
			} //else
			if (imageInput == null || this.props.forceKeepDropzone) {
				let maxImgSize = c.MAX_IMAGE_SIZE;
				if (maxImageSize) {
					maxImgSize = maxImageSize;
				}

				// show dropzone control
				const dropZoneProps: any = {
					accept: 'image/jpeg, image/png',
					multiple: false,
					maxSize: maxImgSize,
					onDrop: (acceptedFiles, fileRejections) => {
						if (acceptedFiles.length > 0) {
							customUploadAction ? customUploadAction() : this.props.meta.dispatch(this.uploadImage(acceptedFiles));
						} else if (fileRejections.length > 0 && this.props.imageUploadFailed) {
							if (fileRejections[0].file.size > maxImgSize) {
								this.props.imageUploadFailed({
									status: 422,
									statusText: t('ImageUpload_Error_ImageSizeTooLarge'),
									error: [t('ImageUpload_Error_ImageSizeTooLarge', { count: formatInteger(maxImgSize / 1000) })],
								});
							} else if (['image/jpeg', 'image/png'].indexOf(fileRejections[0].file.type) === -1) {
								this.props.imageUploadFailed({
									status: 422,
									statusText: t('ImageUpload_Error_ImageTypeNotSupported'),
									error: [t('ImageUpload_Error_ImageTypeNotSupported')],
								});
							}
						}
					},
				};

				if (this.props.onFileDialogCancel) {
					dropZoneProps.onFileDialogCancel = this.props.onFileDialogCancel;
				}

				let dropzoneWrapperClassName: any = 'dropZoneHolder dropZoneHolderPrimary';
				let dropzoneClassName: any = 'dropZoneControl';

				// show instructions text as default -> if get ref from props, remove instructions text as opening of file dialog will be handled outside component
				let instructions: JSX.Element = <div className="instructions">{t('FileInput_Instructions')}</div>;
				if (this.props.dropZoneRef) {
					instructions = null;
					dropZoneProps.ref = this.props.dropZoneRef;
					dropzoneClassName = '';
					dropzoneWrapperClassName = 'dropZoneHolder d-flex justify-content-center';
				}
				if (imageInput && this.props.forceKeepDropzone) {
					dropzoneWrapperClassName = 'd-none';
				}

				imageInput = (
					<>
						{this.props.forceKeepDropzone && imageInput}
						<div className={dropzoneWrapperClassName}>
							<Dropzone {...dropZoneProps}>
								{({ getRootProps, getInputProps }) => (
									<div {...getRootProps({ className: dropzoneClassName })}>
										{uploadImageIcon}
										{imageDescription || uploadDescription ? (
											<div className="imgDropTxt">
												{uploadDescription && uploadDescription.length > 0 ? (
													<div>
														<label className="uploadDescription">{uploadDescription}</label>
													</div>
												) : null}
												{imageDescription && imageDescription.length > 0 ? (
													<div>
														<label className="imgDescription">{imageDescription}</label>
													</div>
												) : null}
											</div>
										) : null}
										{instructions}
										<input
											{...getInputProps({ name: field.name })}
											onClick={
												customUploadAction
													? (e) => {
															e.preventDefault(), customUploadAction();
													  }
													: () => getInputProps({ onClick: field.onClick })
											}
										/>
									</div>
								)}
							</Dropzone>
							{plainInput && field.meta.touched && field.meta.error && (
								<span style={{ color: 'red' }}>{field.meta.error}</span>
							)}
						</div>
					</>
				);
			}
		}

		let labelContent: JSX.Element = null;
		if (label && label.length) {
			labelContent = <PortalFieldLabel name={input.name} text={label} />;
		}

		let content: JSX.Element = null;
		// show only plain image input without loader and additional styles
		if (plainInput) {
			content = imageInput;
		} else {
			let holderClasses = 'uploadImageHolder';
			if (pictureUrl.length > 0) {
				holderClasses = `uploadImageHolder hasImage`;
			}
			content = (
				<div className={holderClasses + ' ' + (className || '')}>
					{!this.props.hideImageInput ? labelContent : null}
					<LoadingOverlayWrapper active={renderLoader} spinner={true} text={t('BSPortal_Common_Loading')}>
						{!this.props.hideImageInput ? imageInput : null}
						{field.meta.touched && field.meta.error && <span style={{ color: 'red' }}>{field.meta.error}</span>}
						{pictureOrCrop}
					</LoadingOverlayWrapper>
				</div>
			);
		}

		return content;
	}
}
export { ImageUpload };
